import { Button, Input } from '@roger/ui-components';
import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import type { ClinicEnrollmentTask } from '../../services/clinicsApi';
import { completeEnrollmentTask } from '../../services/clinicsApi';
import { useStore } from '../../store';

interface EnrollmentTaskFormProps {
  recordId: string;
  task: ClinicEnrollmentTask;
  onSent: () => void;
  /** Who the answer is for, shown beside a one-box step: the clinic's name. */
  label?: string;
}

/**
 * Answer one enrollment step from the tab — an NGS packet ID, say — instead of
 * sending it to someone at Roger to type into Stedi.
 *
 * Only rendered for a step the backend marks `answerable`: the next open one,
 * plain text only, never bank or tax detail. The confirm step is there because
 * the answer goes to the payer in the agency's name and cannot be taken back.
 */
export function EnrollmentTaskForm({
  recordId,
  task,
  onSent,
  label,
}: EnrollmentTaskFormProps) {
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );
  const fields = task.fields ?? [];
  const [values, setValues] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const complete = fields.every(field => values[field.key]?.trim());

  const send = async () => {
    if (!task.task_id) return;
    setSending(true);
    try {
      await completeEnrollmentTask(recordId, task.task_id, values);
      showSuccess(
        fields.length ? 'Sent. This step is done.' : 'Marked as done.'
      );
      onSent();
    } catch (error) {
      console.error('Failed to send enrollment step', error);
      showError(
        error instanceof Error
          ? error.message
          : "Couldn't send this step. Please try again."
      );
      setConfirming(false);
    } finally {
      setSending(false);
    }
  };

  const setValue = (key: string, value: string) =>
    setValues(current => ({ ...current, [key]: value }));

  // One box, the common case: the clinic, the box and the button on one line,
  // with the field's name inside the box.
  if (fields.length === 1 && label) {
    const [field] = fields;
    const name = field.label ?? field.key;
    return (
      <div className='space-y-1.5'>
        <div className='flex items-center gap-2'>
          <span className='min-w-0 flex-1'>{label}</span>
          <Input
            inputSize='sm'
            aria-label={`${name} for ${label}`}
            placeholder={name}
            value={values[field.key] ?? ''}
            disabled={confirming || sending}
            onChange={event => setValue(field.key, event.target.value)}
            className='w-40 shrink-0'
          />
          {confirming ? (
            <>
              <Button size='sm' onClick={() => void send()} disabled={sending}>
                {sending ? 'Sending…' : 'Send'}
              </Button>
              <Button
                size='sm'
                variant='outline'
                onClick={() => setConfirming(false)}
                disabled={sending}
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button
              size='sm'
              onClick={() => setConfirming(true)}
              disabled={!complete}
            >
              Send to payer
            </Button>
          )}
        </div>
        {confirming && (
          <p className='text-right text-xs text-muted-foreground'>
            This goes to the payer and can&apos;t be changed afterwards.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className='space-y-2'>
      {label && <p className='font-medium'>{label}</p>}
      {fields.map(field => (
        <label key={field.key} className='block space-y-1'>
          <span className='text-xs font-medium'>
            {field.label ?? field.key}
          </span>
          <Input
            inputSize='sm'
            value={values[field.key] ?? ''}
            disabled={confirming || sending}
            onChange={event =>
              setValues(current => ({
                ...current,
                [field.key]: event.target.value,
              }))
            }
          />
        </label>
      ))}
      {confirming ? (
        <div className='space-y-2'>
          <p className='text-muted-foreground'>
            {fields.length
              ? "This goes to the payer and can't be changed afterwards."
              : "Only mark this done once you've finished the steps above. It can't be undone."}
          </p>
          <div className='flex gap-2'>
            <Button size='sm' onClick={() => void send()} disabled={sending}>
              {sending ? 'Sending…' : fields.length ? 'Send' : 'Mark done'}
            </Button>
            <Button
              size='sm'
              variant='outline'
              onClick={() => setConfirming(false)}
              disabled={sending}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          size='sm'
          onClick={() => setConfirming(true)}
          disabled={!complete}
        >
          {fields.length ? 'Send to payer' : "I've done this"}
        </Button>
      )}
    </div>
  );
}
